import { themeBoxShadow, themeColor } from '@config/theme';
import { useWindowSize } from '@hooks/useWindowSize';
import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { chatService } from 'services/chat';
import { useUser } from 'services/users';
import { ChannelList } from '../components/ChannelList';
import { useDefaultChannels } from '../hooks';
import { useChannels } from '../hooks/useChannels';
import { useChannelData } from '../hooks/useChannelData';
import { useUserChatQueryParams } from '../hooks/useChatQueryParams';
import { useChatStore, useMessageStore } from '../store';
import { ChannelType } from '../types';
import { SidebarSearch } from './SidebarSearch';
import { TabNavigation } from './TabNavigation';

const styles = {
	sidebarContainer: {
		width: {
			md: '380px',
		},
		display: {
			sm: 'block',
		},
		boxShadow: themeBoxShadow.main,
		backgroundColor: themeColor.cream,
		position: 'sticky',
		height: '100%',
	},
};

const ChatSidebar: React.FC = () => {
	const [_, setSearchParams] = useSearchParams();
	const { channelId: urlChannelId, tabType } = useUserChatQueryParams();
	const {
		activeTab,
		setActiveTab,
		setSelectedChannelId,
		selectedChannelId,
		lastMessageChannel,
		lastMessageTimestamp,
		searchQuery,
		setSearchQuery,
	} = useChatStore();
	const { data: user } = useUser();
	const size = useWindowSize();

	const {
		channels: defaultApiChannels,
		isLoading: isLoadingDefaultChannels,
		mutateDefaultChannels,
	} = useDefaultChannels();

	const {
		channels: apiChannels,
		isLoading,
		loadMore,
		mutateChannels,
		isReachingEnd,
	} = useChannels(activeTab, searchQuery);

	// Use the custom hook for channel data processing
	const { filteredDefaultChannels, sortedChannels } = useChannelData({
		apiChannels: apiChannels || [],
		defaultApiChannels: defaultApiChannels || [],
		activeTab,
		lastMessageChannel,
		lastMessageTimestamp,
	});

	// Client-side filtering for default channels when searching
	const searchFilteredDefaultChannels = useMemo(() => {
		if (!searchQuery.trim()) return filteredDefaultChannels;
		
		const query = searchQuery.toLowerCase();
		return filteredDefaultChannels.filter(channel => 
			channel.name?.toLowerCase().includes(query)
		);
	}, [filteredDefaultChannels, searchQuery]);


	// Force refresh channels when user data loads
	useEffect(() => {
		if (user?.user_id) {
			mutateChannels();
			mutateDefaultChannels();
		}
	}, [user?.user_id, mutateChannels, mutateDefaultChannels]);

	// Handle URL params for channel and tab selection
	useEffect(() => {
		if (tabType && tabType !== activeTab) {
			setActiveTab(tabType);
			// Clear search when changing tabs
			setSearchQuery('');
		}

		if (urlChannelId && urlChannelId !== selectedChannelId) {
			setSelectedChannelId(urlChannelId);
		}
	}, [urlChannelId, tabType, activeTab, selectedChannelId, setSelectedChannelId, setActiveTab, setSearchQuery]);

	// Optimize channel updates when a new message arrives
	const lastRefetchTime = useRef<number>(Date.now());
	useEffect(() => {
		if (lastMessageTimestamp && lastMessageChannel) {
			// Always update the specific channel that received a message
			mutateChannels((prevData) => {
				if (!prevData || !prevData.length) return prevData;

				// Get latest pending message for this channel if available
				const pendingMessages = useMessageStore.getState().getPendingMessages(lastMessageChannel);
				const latestMessage =
					pendingMessages && pendingMessages.length > 0
						? pendingMessages[pendingMessages.length - 1]
						: null;

				// Update all pages where the channel appears
				return prevData.map((page) => {
					if (!page || !Array.isArray(page)) return page;

					// Find and update the specific channel
					return page.map((channel) => {
						if (channel.id === lastMessageChannel) {
							// Update with latest message info if available
							const updatedChannel = { ...channel };

							// If we have a new message, update message ID and content
							if (latestMessage) {
								// Create a simplified last message format for display
								if (latestMessage.contents && latestMessage.contents.length > 0) {
									const messageContent = JSON.stringify(latestMessage.contents);
									updatedChannel.lastMessage = {
										id: latestMessage.id,
										content: messageContent,
										createdAt: Math.floor(Date.now() / 1000),
										attachments: latestMessage.attachments || [],
										status: latestMessage.messageStatus || 0,
									};
								}
							}

							return updatedChannel;
						}
						return channel;
					});
				});
			}, false); // false means don't revalidate immediately

			const REFETCH_THRESHOLD = 30000; // 30 seconds
			const now = Date.now();
			const timeSinceLastRefetch = now - lastRefetchTime.current;

			if (timeSinceLastRefetch > REFETCH_THRESHOLD) {
				mutateChannels();
				lastRefetchTime.current = now;
			}
		}
	}, [lastMessageTimestamp, lastMessageChannel, mutateChannels]);

	const handleChannelSelect = async (channelId: string, channelType: ChannelType) => {
		setSelectedChannelId(channelId);
		let tab = 'direct';
		if (
			channelType === ChannelType.GroupMessaging ||
			channelType === ChannelType.ChannelMessaging ||
			channelType === ChannelType.TelagriNewsFarmerChannel ||
			channelType === ChannelType.TelagriNewsSpecialistChannel
		) {
			tab = 'groups';
		} else if (channelType === ChannelType.DirectReadonly) {
			tab = 'completed';
		}
		setSearchParams({ channelId: channelId, tab: tab });

		await chatService.seenChannel(channelId);
		mutateChannels();
	};

	const calculatedMinHeight = useMemo(() => {
		if (size.width <= 576) return `calc(${size.height}px - 288px)`;
		if (size.width <= 993 && size.width > 576) return `calc(${size.height}px - 220px)`;
		if (size.width > 993) return `calc(${size.height}px - 255px)`;
		return `calc(${size.height}px - 164px)`;
	}, [size.height, size.width]);

	// Handle scroll for infinite loading
	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		// Only load more if we're near the bottom, have more channels to load, and not already loading
		if (scrollHeight - scrollTop - clientHeight < 50 && !isReachingEnd && !isLoading) {
			loadMore();
		}
	};

	const handleRefresh = () => {
		mutateChannels();
		mutateDefaultChannels();
	};

	return (
		<Box position="relative" sx={{ height: '100%' }}>
			<Box sx={styles.sidebarContainer}>
				<TabNavigation />
				{/* enable for search */}
				{/* <SidebarSearch /> */}
				<Box
					height={calculatedMinHeight}
					className="overflow-y-auto no-scrollbar"
					onScroll={handleScroll}
				>
					{isLoading && isLoadingDefaultChannels ? (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<CircularProgress />
						</Box>
					) : (
						<Box>
							{/* Default Channels */}
							{searchFilteredDefaultChannels && searchFilteredDefaultChannels.length > 0 && <ChannelList
								channels={searchFilteredDefaultChannels}
								selectedChannelId={selectedChannelId}
								onChannelSelect={handleChannelSelect}
								isLoading={isLoadingDefaultChannels}
								emptyStateText=""
							/>}

							{/* Regular Channels */}
							<ChannelList
								channels={sortedChannels}
								selectedChannelId={selectedChannelId}
								onChannelSelect={handleChannelSelect}
								isLoading={isLoading}
								emptyStateText={
									!isLoadingDefaultChannels && searchFilteredDefaultChannels.length === 0
										? searchQuery.trim() ? 'No channels found' : 'No channels available'
										: ''
								}
								onRefresh={
									!isLoading &&
									!isLoadingDefaultChannels &&
									searchFilteredDefaultChannels.length === 0 &&
									sortedChannels.length === 0 &&
									!searchQuery.trim()
										? handleRefresh
										: undefined
								}
							/>

							{/* Loading indicator for infinite scroll */}
							{isLoading && !isReachingEnd && sortedChannels.length > 0 && (
								<Box display="flex" justifyContent="center" p={2}>
									<CircularProgress size={24} />
								</Box>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default ChatSidebar;
